import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { loadTenantConfig, getCurrentDomain, clearTenantCache } from '../../utils/configLoader';

const TenantContext = createContext();

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyBranding = useCallback((branding) => {
    if (!branding) {
      console.warn('[Tenant] No branding configuration found');
      return;
    }

    // If the config includes a full named-color map (static JSON tenants), apply those first.
    // This gives the most precise control over every color in the app.
    if (branding.colors) {
      Object.entries(branding.colors).forEach(([key, value]) => {
        const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        document.documentElement.style.setProperty(cssVarName, value);
      });
    }

    // Always apply primaryColor and secondaryColor.
    // These also drive the named app colors (caribbeanCurrent, adminBlue, tangerine, orange)
    // so that tenants onboarded via the form get correct colors without a full colors map.
    if (branding.primaryColor) {
      document.documentElement.style.setProperty('--color-caribbean-current', branding.primaryColor);
      document.documentElement.style.setProperty('--color-admin-blue', branding.primaryColor);
    }

    if (branding.secondaryColor) {
      document.documentElement.style.setProperty('--color-tangerine', branding.secondaryColor);
      document.documentElement.style.setProperty('--color-orange', branding.secondaryColor);
    }
    
    // Set favicon
    if (branding.faviconUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'shortcut icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = branding.faviconUrl;
    }
    
    console.log('[Tenant] Branding applied:', {
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
      customColors: branding.colors ? Object.keys(branding.colors).length : 0,
      favicon: branding.faviconUrl ? 'Updated' : 'Not set'
    });
  }, []);

  const initializeTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const domain = getCurrentDomain();
      console.log('[Tenant] Initializing for domain:', domain);
      
      // Load everything from frontend config file
      const tenantData = await loadTenantConfig(domain);
      
      if (!tenantData) {
        throw new Error('Failed to load tenant configuration');
      }
      
      setTenant(tenantData);
      
      // Apply branding
      applyBranding(tenantData.branding);
      
      // Set page title
      document.title = `${tenantData.companyName} - Risk Management`;
      
      setLoading(false);
      
      console.log('[Tenant] Initialized successfully:', tenantData.companyName);
    } catch (err) {
      console.error('[Tenant] Failed to initialize:', err);
      setError(err.message || 'Failed to load tenant configuration');
      setLoading(false);
    }
  }, [applyBranding]);

  useEffect(() => {
    initializeTenant();
  }, [initializeTenant]);

  // Applies an edited part of the risk config (e.g. perspectives from Settings)
  // without the full reload, and drops the cached copy so the next load is fresh.
  const updateConfig = useCallback((partial) => {
    setTenant((prev) => (prev ? { ...prev, config: { ...prev.config, ...partial } } : prev));
    clearTenantCache();
  }, []);

  const value = {
    tenant,
    loading,
    error,
    reload: initializeTenant,
    updateConfig,
    // Convenience accessors
    companyName: tenant?.companyName,
    branding: tenant?.branding,
    config: tenant?.config,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}

// Export context for direct access if needed
export default TenantContext;
